// USD price input that stores dollars as a local draft string so typing
// behaves like a normal number field. The parent tracks cents; this
// component syncs one-way on mount / on external cents change, but does
// NOT clobber the draft while the user is editing.
//
// Fixes the old pattern where the input's `value` was always
// `(cents/100).toFixed(2)`, so typing "5" appended into "0.00" and
// produced "0.005" → 1 cent.
import { useEffect, useState } from 'react';

/** Cents integer → draft string ("" for null/0-with-no-draft, "0.00" for explicit zero after edit). */
function centsToDraft(cents) {
  if (cents == null) return '';
  if (cents === 0) return '';
  return (cents / 100).toFixed(2);
}

/** Draft string → cents integer, or null if we can't parse (leaves parent state untouched). */
function draftToCents(draft) {
  const s = String(draft).trim();
  if (s === '') return 0;
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

export default function PriceInput({
  cents,
  onChangeCents,
  placeholder = '0.00',
  disabled,
  className = 'input',
  style,
  ...rest
}) {
  const [draft, setDraft] = useState(() => centsToDraft(cents));

  // Resync when the parent hands us a materially different cents value
  // (e.g. loading an existing entity into an editor). Skip when the
  // current draft already round-trips to the same cents — otherwise
  // typing "12" would flicker back to "0.12".
  useEffect(() => {
    const parsed = draftToCents(draft);
    if (parsed !== cents) setDraft(centsToDraft(cents));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cents]);

  const onChange = (e) => {
    // Keep digits, one decimal point, and only two decimal places.
    let v = e.target.value.replace(/[^0-9.]/g, '');
    const firstDot = v.indexOf('.');
    if (firstDot !== -1) {
      // Strip any further dots and clamp to 2 decimal places.
      v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, '').slice(0, 2);
    }
    setDraft(v);
    const parsed = draftToCents(v);
    if (parsed != null) onChangeCents(parsed);
  };

  const onBlur = () => {
    // Normalize the draft to "N.NN" when it parses to something valid.
    const parsed = draftToCents(draft);
    if (parsed != null && parsed > 0) setDraft((parsed / 100).toFixed(2));
    else if (parsed === 0) setDraft('');
  };

  return (
    <input
      className={className}
      style={style}
      inputMode="decimal"
      placeholder={placeholder}
      value={draft}
      onChange={onChange}
      onBlur={onBlur}
      disabled={disabled}
      {...rest}
    />
  );
}
